import { Alert, Skeleton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { BarChart3 } from 'lucide-react';
import { FEATURE_RANGE_OPTIONS } from '../constants';
import type { FeatureGroupBy, FeatureUsageReport } from '../types';
import { buildDonutGradient, sliceColor } from '../utils';
import { formatAdminNumber } from '../../../../utils/numberFormat';

interface FeatureUsageChartProps {
  report?: FeatureUsageReport;
  isLoading: boolean;
  errorMessage?: string;
  rangeDays: number;
  groupBy: FeatureGroupBy;
  onRangeChange: (days: number) => void;
  onGroupByChange: (groupBy: FeatureGroupBy) => void;
  /** Distinct users per slice only make sense across the whole platform. */
  showUniqueUsers?: boolean;
}

const text = {
  calls: 'calls',
  empty: 'No tracked calls in this period.',
  failed: 'failed',
  groupBy: { feature: 'Feature', group: 'Group' },
  users: 'users',
} as const;

const toggleSx = {
  '& .MuiToggleButton-root': { textTransform: 'none', px: 1.5, py: 0.25 },
} as const;

export const FeatureUsageChart = ({
  report,
  isLoading,
  errorMessage,
  rangeDays,
  groupBy,
  onRangeChange,
  onGroupByChange,
  showUniqueUsers = false,
}: FeatureUsageChartProps) => {
  const items = report?.items ?? [];
  const total = report?.totalCount ?? 0;

  return (
    <div className="min-w-0">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <ToggleButtonGroup
          exclusive
          size="small"
          value={rangeDays}
          onChange={(_, value: number | null) => value && onRangeChange(value)}
          aria-label="Feature usage period"
          sx={toggleSx}
        >
          {FEATURE_RANGE_OPTIONS.map((option) => (
            <ToggleButton key={option.days} value={option.days}>
              {option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={groupBy}
          onChange={(_, value: FeatureGroupBy | null) => value && onGroupByChange(value)}
          aria-label="Group feature usage by"
          sx={toggleSx}
        >
          <ToggleButton value="feature">{text.groupBy.feature}</ToggleButton>
          <ToggleButton value="group">{text.groupBy.group}</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {errorMessage ? (
        <Alert severity="error">{errorMessage}</Alert>
      ) : isLoading ? (
        <Skeleton variant="rounded" height={260} />
      ) : total === 0 ? (
        <div className="flex min-h-[200px] flex-col items-center justify-center gap-2 text-sm text-gray-500 dark:text-slate-400">
          <BarChart3 size={28} aria-hidden />
          {text.empty}
        </div>
      ) : (
        <div className="flex flex-col items-center gap-6 md:flex-row md:items-start">
          <div
            className="relative h-48 w-48 shrink-0 rounded-full"
            style={{ background: buildDonutGradient(items, total) }}
            role="img"
            aria-label={`${formatAdminNumber(total)} ${text.calls}`}
          >
            <div className="absolute inset-9 flex flex-col items-center justify-center rounded-full bg-white dark:bg-slate-800">
              <span className="text-xl font-bold text-gray-900 dark:text-slate-100">
                {formatAdminNumber(total)}
              </span>
              <span className="text-xs text-gray-500 dark:text-slate-400">{text.calls}</span>
            </div>
          </div>

          <ul className="grid w-full min-w-0 grid-cols-1 gap-2">
            {items.map((item, index) => (
              <li key={item.code} className="flex min-w-0 items-center gap-3 text-sm">
                <span
                  className="h-3 w-3 shrink-0 rounded-sm"
                  style={{ backgroundColor: sliceColor(item, index) }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1 truncate font-medium text-gray-900 dark:text-slate-100">
                  {item.name}
                </span>
                <span className="shrink-0 tabular-nums text-gray-600 dark:text-slate-300">
                  {formatAdminNumber(item.count)} · {item.percent}%
                </span>
                {(item.failed > 0 || showUniqueUsers) && (
                  <span className="hidden shrink-0 text-xs text-gray-400 dark:text-slate-500 sm:inline">
                    {showUniqueUsers && `${formatAdminNumber(item.uniqueUsers)} ${text.users}`}
                    {showUniqueUsers && item.failed > 0 && ' · '}
                    {item.failed > 0 && `${formatAdminNumber(item.failed)} ${text.failed}`}
                  </span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
